import { escapeHtml } from '../utils/dom.js';
import { MATERIAL_BY_ID } from '../data/materials.js';

const PALETTES = [
  ['#e7f0ff', '#0b5cff', '#12a98b', '#f59e0b'],
  ['#fff1e7', '#c84b31', '#1f6d61', '#f2bb4b'],
  ['#edf7ef', '#317a57', '#163f67', '#d59b47'],
  ['#f4efff', '#7550c7', '#1a88a8', '#ef8d68'],
  ['#e9f5f7', '#176879', '#e16842', '#f3c969']
];

function windowGrid(x, y, columns, rows, color = '#b9e3ef') {
  let shapes = '';
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      shapes += `<rect x="${x + column * 29}" y="${y + row * 25}" width="19" height="15" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>`;
    }
  }
  return shapes;
}

function genericHouseScene(template, colors, villa = false) {
  const [, primary, accent, warm] = colors;
  const wide = villa ? 250 : 205;
  const left = (360 - wide) / 2;
  const roofY = villa ? 92 : 78;
  return `
    <path d="M38 256h284" stroke="#93b18e" stroke-width="14" stroke-linecap="round"/>
    <path d="M${left - 16} ${roofY + 62} 180 ${roofY - 12} ${left + wide + 16} ${roofY + 62}" fill="${primary}" stroke="#173557" stroke-width="5" stroke-linejoin="round"/>
    <path d="M${left} ${roofY + 58}h${wide}v116H${left}z" fill="#fffaf1" stroke="#173557" stroke-width="5"/>
    <rect x="158" y="${roofY + 113}" width="44" height="61" rx="3" fill="${warm}" stroke="#173557" stroke-width="4"/>
    <rect x="${left + 20}" y="${roofY + 92}" width="52" height="38" rx="4" fill="#a8dbea" stroke="#173557" stroke-width="4"/>
    <rect x="${left + wide - 72}" y="${roofY + 92}" width="52" height="38" rx="4" fill="#a8dbea" stroke="#173557" stroke-width="4"/>
    ${villa ? `<rect x="${left + 76}" y="${roofY + 73}" width="${wide - 152}" height="44" rx="4" fill="#bce9f1" stroke="#173557" stroke-width="4"/><path d="M220 244h90v13h-90z" fill="${accent}"/><path d="M226 235h78v10h-78z" fill="#42b9d7" opacity=".72"/>` : ''}
    <circle cx="72" cy="226" r="22" fill="${accent}"/><rect x="68" y="226" width="8" height="31" rx="4" fill="#76523d"/>
    <circle cx="292" cy="232" r="18" fill="${accent}"/><rect x="289" y="232" width="6" height="25" rx="3" fill="#76523d"/>
  `;
}

function materialColor(materialId, fallback) {
  return MATERIAL_BY_ID.get(materialId)?.color ?? fallback;
}

function shade(hex, delta) {
  const clean = String(hex).replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(clean)) return hex;
  const value = Number.parseInt(clean, 16);
  const channel = (shift) => Math.max(0, Math.min(255, ((value >> shift) & 255) + delta));
  return `#${[channel(16), channel(8), channel(0)].map((item) => item.toString(16).padStart(2, '0')).join('')}`;
}

const SOFT_EDGE = 'rgba(35,43,46,.22)';

function isoPoint(x, z, y = 0) {
  return [180 + (x - z) * 8.75, 220 + (x + z) * 3.8 - y * 17.4];
}

function svgPoints(points) {
  return points.map((point) => isoPoint(...point).map((value) => value.toFixed(1)).join(',')).join(' ');
}

function polygon(points, fill, stroke = SOFT_EDGE, strokeWidth = 0.46, extra = '') {
  return `<polygon points="${svgPoints(points)}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="round" ${extra}/>`;
}

function polyline(points, stroke, strokeWidth = 0.5, opacity = 1) {
  return `<polyline points="${svgPoints(points)}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" opacity="${opacity}"/>`;
}

function isoBox({ cx = 0, cz = 0, y = 0, width = 1, depth = 1, height = 1, color = '#ddd', top, front, side, stroke = SOFT_EDGE, strokeWidth = 0.46 }) {
  const x0 = cx - width / 2;
  const x1 = cx + width / 2;
  const z0 = cz - depth / 2;
  const z1 = cz + depth / 2;
  const y1 = y + height;
  return [
    polygon([[x1, z0, y], [x1, z1, y], [x1, z1, y1], [x1, z0, y1]], side ?? shade(color, -25), stroke, strokeWidth),
    polygon([[x0, z1, y], [x1, z1, y], [x1, z1, y1], [x0, z1, y1]], front ?? shade(color, -4), stroke, strokeWidth),
    polygon([[x0, z0, y1], [x1, z0, y1], [x1, z1, y1], [x0, z1, y1]], top ?? shade(color, 16), stroke, strokeWidth)
  ].join('');
}

function faceRectZ(cx, z, y, width, height, fill, stroke = SOFT_EDGE, strokeWidth = 0.42) {
  const x0 = cx - width / 2;
  const x1 = cx + width / 2;
  return polygon([[x0, z, y], [x1, z, y], [x1, z, y + height], [x0, z, y + height]], fill, stroke, strokeWidth);
}

function faceRectX(x, cz, y, depth, height, fill, stroke = SOFT_EDGE, strokeWidth = 0.42) {
  const z0 = cz - depth / 2;
  const z1 = cz + depth / 2;
  return polygon([[x, z0, y], [x, z1, y], [x, z1, y + height], [x, z0, y + height]], fill, stroke, strokeWidth);
}

function frontWindow(cx, z, y, width, height, glassFill, deep = false) {
  const frame = deep ? '#403d39' : '#4d5658';
  const innerWidth = Math.max(0.3, width - 0.18);
  const innerHeight = Math.max(0.3, height - 0.18);
  let result = faceRectZ(cx, z, y, width, height, frame, 'rgba(20,25,27,.32)', 0.45);
  result += faceRectZ(cx, z + 0.012, y + 0.09, innerWidth, innerHeight, glassFill, 'rgba(255,255,255,.3)', 0.25);
  result += faceRectZ(cx, z + 0.018, y + height * 0.49, 0.055, innerHeight, '#485256', 'none', 0);
  result += polyline([[cx - innerWidth * 0.42, z + 0.02, y + height - 0.18], [cx + innerWidth * 0.2, z + 0.02, y + height - 0.18]], '#d8edf0', 0.65, 0.58);
  return result;
}

function sideWindow(x, cz, y, depth, height, glassFill) {
  const innerDepth = Math.max(0.3, depth - 0.16);
  let result = faceRectX(x, cz, y, depth, height, '#424c4f', 'rgba(20,25,27,.3)', 0.42);
  result += faceRectX(x + 0.012, cz, y + 0.08, innerDepth, height - 0.16, glassFill, 'rgba(255,255,255,.25)', 0.22);
  result += faceRectX(x + 0.018, cz, y + height * 0.49, 0.05, height - 0.2, '#4d585b', 'none', 0);
  return result;
}

function frontDoor(cx, z, y, width, height, color, glassFill, modern) {
  let result = faceRectZ(cx, z, y, width, height, shade(color, -17), 'rgba(25,28,29,.3)', 0.45);
  result += faceRectZ(cx, z + 0.014, y + 0.1, width - 0.16, height - 0.16, color, 'rgba(255,255,255,.18)', 0.25);
  if (modern) result += faceRectZ(cx + width * 0.18, z + 0.02, y + 0.35, width * 0.28, height * 0.58, glassFill, 'none', 0);
  const [handleX, handleY] = isoPoint(cx + width * 0.31, z + 0.03, y + height * 0.51);
  result += `<circle cx="${handleX.toFixed(1)}" cy="${handleY.toFixed(1)}" r="1.05" fill="#d7c7a6"/>`;
  return result;
}

function claddingLines(cx, z, y, width, height, vertical = false) {
  let result = '';
  if (vertical) {
    const count = Math.max(3, Math.floor(width / 0.34));
    for (let index = 1; index < count; index += 1) {
      const x = cx - width / 2 + (width / count) * index;
      result += polyline([[x, z, y], [x, z, y + height]], '#3f2c20', 0.32, 0.28);
    }
  } else {
    const count = Math.max(4, Math.floor(height / 0.32));
    for (let index = 1; index < count; index += 1) {
      const lineY = y + (height / count) * index;
      result += polyline([[cx - width / 2, z, lineY], [cx + width / 2, z, lineY]], '#402d22', 0.3, 0.25);
    }
  }
  return result;
}

function hipRoof(cx, cz, y, width, depth, height, color, paint = {}) {
  const x0 = cx - width / 2;
  const x1 = cx + width / 2;
  const z0 = cz - depth / 2;
  const z1 = cz + depth / 2;
  const ridgeAlongX = width >= depth;
  let result;
  if (ridgeAlongX) {
    const ridge = Math.max(0.45, (width - depth) * 0.44);
    const r0 = [cx - ridge / 2, cz, y + height];
    const r1 = [cx + ridge / 2, cz, y + height];
    result = [
      polygon([[x0, z0, y], [x1, z0, y], r1, r0], paint.back ?? shade(color, -16)),
      polygon([[x1, z0, y], [x1, z1, y], r1], paint.side ?? shade(color, -27)),
      polygon([[x0, z1, y], [x1, z1, y], r1, r0], paint.front ?? shade(color, 5)),
      polygon([[x0, z0, y], [x0, z1, y], r0], paint.light ?? shade(color, -7)),
      polyline([[x0, z1, y], [x1, z1, y]], shade(color, -38), 0.75, 0.68),
      polyline([r0, r1], shade(color, 28), 0.62, 0.62)
    ].join('');
  } else {
    const ridge = Math.max(0.45, (depth - width) * 0.44);
    const r0 = [cx, cz - ridge / 2, y + height];
    const r1 = [cx, cz + ridge / 2, y + height];
    result = [
      polygon([[x0, z0, y], [x1, z0, y], r0], paint.back ?? shade(color, -16)),
      polygon([[x1, z0, y], [x1, z1, y], r1, r0], paint.side ?? shade(color, -27)),
      polygon([[x0, z1, y], [x1, z1, y], r1], paint.front ?? shade(color, 5)),
      polygon([[x0, z0, y], [x0, z1, y], r1, r0], paint.light ?? shade(color, -7)),
      polyline([[x0, z1, y], [x1, z1, y]], shade(color, -38), 0.75, 0.68),
      polyline([r0, r1], shade(color, 28), 0.62, 0.62)
    ].join('');
  }
  return result;
}

function gableRoof(cx, cz, y, width, depth, height, color, paint = {}) {
  const x0 = cx - width / 2;
  const x1 = cx + width / 2;
  const z0 = cz - depth / 2;
  const z1 = cz + depth / 2;
  const ridge0 = [cx, z0, y + height];
  const ridge1 = [cx, z1, y + height];
  return [
    polygon([[x0, z0, y], [x0, z1, y], ridge1, ridge0], paint.light ?? shade(color, 5)),
    polygon([[x1, z0, y], [x1, z1, y], ridge1, ridge0], paint.side ?? shade(color, -23)),
    polygon([[x0, z1, y], [x1, z1, y], ridge1], paint.front ?? shade(color, -2)),
    polyline([ridge0, ridge1], shade(color, 30), 0.7, 0.65),
    polyline([[x0, z1, y], [x1, z1, y]], shade(color, -38), 0.72, 0.65)
  ].join('');
}

function flatRoof(cx, cz, y, width, depth, parapet, membrane) {
  let result = isoBox({ cx, cz, y, width, depth, height: 0.3, color: parapet, strokeWidth: 0.38 });
  result += polygon([
    [cx - width / 2 + 0.38, cz - depth / 2 + 0.38, y + 0.31],
    [cx + width / 2 - 0.38, cz - depth / 2 + 0.38, y + 0.31],
    [cx + width / 2 - 0.38, cz + depth / 2 - 0.38, y + 0.31],
    [cx - width / 2 + 0.38, cz + depth / 2 - 0.38, y + 0.31]
  ], membrane, 'rgba(20,25,27,.14)', 0.35);
  return result;
}

function plant(x, z, scale = 1, tree = false) {
  const [sx, sy] = isoPoint(x, z, 0);
  const trunk = tree ? 17 * scale : 6 * scale;
  const radius = tree ? 8.2 * scale : 5.1 * scale;
  return `<g opacity=".98"><ellipse cx="${(sx + 4 * scale).toFixed(1)}" cy="${(sy + 1.5).toFixed(1)}" rx="${(7 * scale).toFixed(1)}" ry="${(2.4 * scale).toFixed(1)}" fill="#263b2a" opacity=".16"/><path d="M${sx.toFixed(1)} ${sy.toFixed(1)}v-${trunk.toFixed(1)}" stroke="#70523a" stroke-width="${(tree ? 2.4 : 1.5) * scale}"/><ellipse cx="${(sx - radius * 0.45).toFixed(1)}" cy="${(sy - trunk - radius * 0.2).toFixed(1)}" rx="${(radius * 0.75).toFixed(1)}" ry="${(radius * 0.58).toFixed(1)}" fill="#587b48"/><ellipse cx="${(sx + radius * 0.42).toFixed(1)}" cy="${(sy - trunk).toFixed(1)}" rx="${(radius * 0.72).toFixed(1)}" ry="${(radius * 0.62).toFixed(1)}" fill="#78975b"/><ellipse cx="${sx.toFixed(1)}" cy="${(sy - trunk - radius * 0.58).toFixed(1)}" rx="${(radius * 0.72).toFixed(1)}" ry="${(radius * 0.64).toFixed(1)}" fill="#476d43"/><ellipse cx="${(sx - radius * 0.12).toFixed(1)}" cy="${(sy - trunk - radius * 0.76).toFixed(1)}" rx="${(radius * 0.34).toFixed(1)}" ry="${(radius * 0.25).toFixed(1)}" fill="#8aaa69" opacity=".78"/></g>`;
}

function parkedCar(cx, cz, mirror) {
  const body = '#5b6266';
  let result = isoBox({ cx, cz, y: 0.08, width: 1.85, depth: 3.5, height: 0.72, color: body, strokeWidth: 0.35 });
  result += isoBox({ cx, cz: cz - 0.15 * mirror, y: 0.8, width: 1.58, depth: 1.65, height: 0.58, color: '#78878b', top: '#aeb9ba', front: '#758589', side: '#536268', strokeWidth: 0.32 });
  for (const z of [cz - 1.05, cz + 1.05]) {
    const [x, y] = isoPoint(cx + 0.95, z, 0.35);
    result += `<ellipse cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" rx="2.6" ry="4" fill="#252a2b"/>`;
  }
  return result;
}

function houseReferenceScene(template) {
  const profile = template.houseProfile;
  const variation = profile.variation ?? 0;
  const mirror = profile.mirror ? -1 : 1;
  const lowerWidth = 8.45 + variation * 0.24;
  const lowerDepth = 6.45 + (variation % 3) * 0.34;
  const baseY = profile.baseElevation ?? 0;
  const floorHeight = 3;
  const lowerWall = materialColor(profile.wall, '#ddd6c9');
  const upperWall = materialColor(profile.upperWall, '#d8d6cf');
  const accent = materialColor(profile.accent, '#8f603b');
  const roof = materialColor(profile.roof, '#34383d');
  const glass = profile.glazing === 'classic' ? '#91aeb1' : '#536e76';
  const frontZ = lowerDepth / 2;
  const siteX = 7.35;
  const siteZ = 6.15;
  const uid = `house-${template.previewSeed}`;
  const roofPaint = profile.roofTiles ? {
    light: `url(#${uid}-roof-light)`,
    front: `url(#${uid}-roof-main)`,
    side: `url(#${uid}-roof-dark)`,
    back: `url(#${uid}-roof-dark)`
  } : {};
  const siteTop = profile.siteSurface === 'garden' ? '#74845b' : profile.siteSurface === 'mixed' ? '#cbd0c6' : '#d7d8d4';
  let drawing = `
    <defs>
      <filter id="${uid}-shadow" x="-35%" y="-35%" width="180%" height="195%"><feDropShadow dx="4" dy="7" stdDeviation="4.5" flood-color="#273238" flood-opacity=".2"/></filter>
      <filter id="${uid}-site-shadow" x="-30%" y="-50%" width="170%" height="210%"><feGaussianBlur stdDeviation="4"/></filter>
      <linearGradient id="${uid}-wall-front" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${shade(lowerWall, 25)}"/><stop offset="1" stop-color="${shade(lowerWall, -6)}"/></linearGradient>
      <linearGradient id="${uid}-wall-side" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${shade(lowerWall, -8)}"/><stop offset="1" stop-color="${shade(lowerWall, -28)}"/></linearGradient>
      <linearGradient id="${uid}-upper-front" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${shade(upperWall, 26)}"/><stop offset="1" stop-color="${shade(upperWall, -5)}"/></linearGradient>
      <linearGradient id="${uid}-upper-side" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${shade(upperWall, -7)}"/><stop offset="1" stop-color="${shade(upperWall, -27)}"/></linearGradient>
      <linearGradient id="${uid}-glass" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${shade(glass, 42)}"/><stop offset=".42" stop-color="${glass}"/><stop offset="1" stop-color="${shade(glass, -23)}"/></linearGradient>
      <pattern id="${uid}-roof-main" width="7" height="5" patternUnits="userSpaceOnUse" patternTransform="skewX(-17)"><rect width="7" height="5" fill="${shade(roof, 5)}"/><path d="M0 1h7M0 4h7M3.5 1v3" stroke="${shade(roof, -22)}" stroke-width=".34" opacity=".65"/></pattern>
      <pattern id="${uid}-roof-light" width="7" height="5" patternUnits="userSpaceOnUse" patternTransform="skewX(14)"><rect width="7" height="5" fill="${shade(roof, 13)}"/><path d="M0 1h7M0 4h7M3.5 1v3" stroke="${shade(roof, -14)}" stroke-width=".32" opacity=".56"/></pattern>
      <pattern id="${uid}-roof-dark" width="7" height="5" patternUnits="userSpaceOnUse" patternTransform="skewX(12)"><rect width="7" height="5" fill="${shade(roof, -17)}"/><path d="M0 1h7M0 4h7M3.5 1v3" stroke="${shade(roof, -34)}" stroke-width=".32" opacity=".55"/></pattern>
    </defs>
    <ellipse cx="190" cy="250" rx="116" ry="17" fill="#2f393d" opacity=".1" filter="url(#${uid}-site-shadow)"/>
    <g filter="url(#${uid}-shadow)">
      ${isoBox({ cx: 0, cz: 0, y: -0.26, width: siteX * 2, depth: siteZ * 2, height: 0.24, color: siteTop, top: siteTop, front: shade(siteTop, -26), side: shade(siteTop, -37), stroke: 'rgba(35,43,46,.13)', strokeWidth: 0.38 })}
    </g>
    <g data-house-profile="${escapeHtml(profile.profileId)}" filter="url(#${uid}-shadow)">
      ${polygon([[-1.32, frontZ + 0.2, 0.025], [1.32, frontZ + 0.2, 0.025], [1.62, siteZ - 0.15, 0.025], [-1.62, siteZ - 0.15, 0.025]], '#aaa9a2', 'rgba(65,65,62,.16)', 0.34)}
  `;

  if (profile.siteSurface !== 'garden') {
    drawing += polygon([[-siteX + 0.25, -siteZ + 0.25, 0.02], [-siteX * 0.35, -siteZ + 0.25, 0.02], [-siteX * 0.35, siteZ - 0.3, 0.02], [-siteX + 0.25, siteZ - 0.3, 0.02]], '#6d8057', 'none', 0);
  }
  if (profile.pool) {
    drawing += polygon([[-5.9 * mirror, -5.25, 0.08], [-2.05 * mirror, -5.25, 0.08], [-2.05 * mirror, -2.55, 0.08], [-5.9 * mirror, -2.55, 0.08]], `url(#${uid}-glass)`, '#f2f0e8', 1.8);
    drawing += polyline([[-5.5 * mirror, -4.86, 0.1], [-2.45 * mirror, -4.86, 0.1]], '#d7f5f4', 0.62, 0.72);
  }
  drawing += plant(-6.05, -2.75, 0.9, true) + plant(5.75, -1.6, 0.72, false);

  if (profile.stilts) {
    for (const x of [-3.25, 0, 3.25]) {
      for (const z of [-2.35, 2.35]) drawing += isoBox({ cx: x, cz: z, y: 0, width: 0.2, depth: 0.2, height: baseY, color: accent, strokeWidth: 0.28 });
    }
  } else if (profile.plinth) {
    drawing += isoBox({ cx: 0, cz: 0, y: 0, width: lowerWidth + 0.25, depth: lowerDepth + 0.25, height: 0.18, color: '#696a65', strokeWidth: 0.3 });
  }

  drawing += isoBox({
    cx: 0, cz: 0, y: baseY, width: lowerWidth, depth: lowerDepth, height: floorHeight, color: lowerWall,
    front: `url(#${uid}-wall-front)`, side: `url(#${uid}-wall-side)`, top: shade(lowerWall, 22), strokeWidth: 0.38
  });
  if (profile.id.startsWith('heritage')) drawing += claddingLines(0, frontZ + 0.018, baseY + 0.08, lowerWidth, floorHeight - 0.16, false);
  drawing += frontDoor(0, frontZ + 0.032, baseY, 1.34, 2.25, accent, `url(#${uid}-glass)`, !profile.id.startsWith('heritage'));
  const frontWindowWidth = profile.glazing === 'wide' ? 2.08 : profile.glazing === 'vertical' ? 0.9 : 1.34;
  const frontWindowHeight = profile.glazing === 'vertical' ? 1.62 : profile.glazing === 'wide' ? 1.42 : 1.25;
  drawing += frontWindow(-lowerWidth * 0.29, frontZ + 0.04, baseY + 0.93, frontWindowWidth, frontWindowHeight, `url(#${uid}-glass)`, profile.deepFrames);
  drawing += frontWindow(lowerWidth * 0.29, frontZ + 0.04, baseY + 0.93, frontWindowWidth, frontWindowHeight, `url(#${uid}-glass)`, profile.deepFrames);
  drawing += sideWindow(lowerWidth / 2 + 0.035, 0.42, baseY + 0.98, 1.62, 1.3, `url(#${uid}-glass)`);

  if (!profile.id.startsWith('heritage')) {
    const panelX = -mirror * lowerWidth * 0.39;
    drawing += faceRectZ(panelX, frontZ + 0.065, baseY + 0.12, 1.08, 2.7, accent, 'rgba(45,30,20,.22)', 0.34);
    drawing += claddingLines(panelX, frontZ + 0.071, baseY + 0.16, 1, 2.58, true);
  }

  let roofCx = 0;
  let roofCz = 0;
  let roofWidth = lowerWidth + profile.eave * 2;
  let roofDepth = lowerDepth + profile.eave * 2;
  let roofY = baseY + floorHeight;
  if (profile.floors > 1) {
    const upperWidth = lowerWidth * (profile.upperScale?.[0] ?? 0.84);
    const upperDepth = lowerDepth * (profile.upperScale?.[1] ?? 0.82);
    roofCx = (profile.upperOffset?.[0] ?? 0) * 0.9;
    roofCz = (profile.upperOffset?.[1] ?? 0) * 0.9;
    drawing += isoBox({
      cx: roofCx, cz: roofCz, y: roofY, width: upperWidth, depth: upperDepth, height: floorHeight, color: upperWall,
      front: `url(#${uid}-upper-front)`, side: `url(#${uid}-upper-side)`, top: shade(upperWall, 22), strokeWidth: 0.38
    });
    const upperFront = roofCz + upperDepth / 2;
    drawing += frontWindow(roofCx - upperWidth * 0.26, upperFront + 0.04, roofY + 0.92, Math.min(1.88, upperWidth * 0.29), 1.45, `url(#${uid}-glass)`, true);
    drawing += frontWindow(roofCx + upperWidth * 0.26, upperFront + 0.04, roofY + 0.92, Math.min(1.88, upperWidth * 0.29), 1.45, `url(#${uid}-glass)`, true);
    drawing += sideWindow(roofCx + upperWidth / 2 + 0.035, roofCz, roofY + 1, 1.48, 1.35, `url(#${uid}-glass)`);
    if (profile.balcony) {
      const balconyCx = roofCx - mirror * upperWidth * 0.15;
      const balconyWidth = upperWidth * 0.5;
      drawing += isoBox({ cx: balconyCx, cz: upperFront + 0.72, y: roofY + 0.04, width: balconyWidth, depth: 1.35, height: 0.16, color: accent, strokeWidth: 0.32 });
      const railZ = upperFront + 1.37;
      drawing += faceRectZ(balconyCx, railZ, roofY + 0.2, balconyWidth, 0.08, '#4d5556', 'none', 0);
      drawing += faceRectZ(balconyCx, railZ, roofY + 0.88, balconyWidth, 0.075, '#4d5556', 'none', 0);
      for (let index = -3; index <= 3; index += 1) drawing += faceRectZ(balconyCx + index * balconyWidth / 7, railZ, roofY + 0.2, 0.055, 0.75, '#4d5556', 'none', 0);
    }
    if (profile.slatScreen) {
      const slatX = roofCx + mirror * upperWidth * 0.33;
      for (let index = -4; index <= 4; index += 1) drawing += faceRectZ(slatX + index * 0.13, upperFront + 0.065, roofY + 0.2, 0.055, 2.55, accent, 'none', 0);
    }
    roofWidth = upperWidth + profile.eave * 2;
    roofDepth = upperDepth + profile.eave * 2;
    roofY += floorHeight;
  }

  if (profile.veranda) {
    const verandaWidth = lowerWidth * (profile.id.startsWith('heritage') ? 0.78 : 0.58);
    drawing += isoBox({ cx: 0, cz: frontZ + 0.78, y: baseY - 0.08, width: verandaWidth, depth: 1.48, height: 0.17, color: accent, strokeWidth: 0.32 });
    for (const x of [-verandaWidth * 0.44, verandaWidth * 0.44]) drawing += isoBox({ cx: x, cz: frontZ + 1.15, y: baseY, width: 0.11, depth: 0.11, height: 2.32, color: accent, strokeWidth: 0.25 });
    if (profile.id.startsWith('heritage')) {
      drawing += faceRectZ(0, frontZ + 1.48, baseY + 0.65, verandaWidth, 0.09, accent, 'none', 0);
      for (let index = -4; index <= 4; index += 1) drawing += faceRectZ(index * verandaWidth / 9, frontZ + 1.48, baseY + 0.18, 0.055, 0.57, accent, 'none', 0);
    }
  }
  if (profile.carport) {
    const carportX = mirror * (lowerWidth / 2 + 1.58);
    drawing += isoBox({ cx: carportX, cz: 0.3, y: 0.01, width: 2.85, depth: 4.65, height: 0.07, color: '#aaa9a2', strokeWidth: 0.26 });
    if (profile.vehicle) drawing += parkedCar(carportX, 0.35, mirror);
    for (const x of [carportX - 1.22, carportX + 1.22]) {
      for (const z of [-1.55, 1.6]) drawing += isoBox({ cx: x, cz: z, y: 0, width: 0.09, depth: 0.09, height: 2.38, color: accent, strokeWidth: 0.22 });
    }
    drawing += isoBox({ cx: carportX, cz: 0.3, y: 2.38, width: 3.05, depth: 4.85, height: 0.14, color: roof, top: shade(roof, 12), strokeWidth: 0.3 });
  }

  if (profile.roofStyle === 'layered-hip') {
    drawing += hipRoof(roofCx, roofCz, roofY, roofWidth, roofDepth, profile.roofHeight, roof, roofPaint);
    drawing += hipRoof(roofCx, roofCz, roofY + profile.roofHeight * 0.62, roofWidth * 0.54, roofDepth * 0.54, profile.roofHeight * 0.62, shade(roof, 5), roofPaint);
  } else if (profile.roofStyle === 'split-gable') {
    drawing += gableRoof(roofCx - roofWidth * 0.255, roofCz, roofY, roofWidth * 0.5, roofDepth, profile.roofHeight, roof, roofPaint);
    drawing += gableRoof(roofCx + roofWidth * 0.255, roofCz, roofY, roofWidth * 0.5, roofDepth, profile.roofHeight, shade(roof, 3), roofPaint);
  } else if (profile.roofStyle === 'hip') {
    drawing += hipRoof(roofCx, roofCz, roofY, roofWidth, roofDepth, profile.roofHeight, roof, roofPaint);
  } else if (profile.roofStyle === 'shed') {
    drawing += polygon([
      [roofCx - roofWidth / 2, roofCz - roofDepth / 2, roofY + profile.roofHeight],
      [roofCx + roofWidth / 2, roofCz - roofDepth / 2, roofY + 0.12],
      [roofCx + roofWidth / 2, roofCz + roofDepth / 2, roofY + 0.12],
      [roofCx - roofWidth / 2, roofCz + roofDepth / 2, roofY + profile.roofHeight]
    ], shade(roof, 6), SOFT_EDGE, 0.42);
  } else {
    drawing += flatRoof(roofCx, roofCz, roofY, roofWidth, roofDepth, upperWall, shade(roof, -5));
  }

  if (baseY > 0.25) {
    for (let index = 0; index < 5; index += 1) drawing += isoBox({ cx: 0, cz: frontZ + 1.05 + index * 0.25, y: index * baseY / 5, width: 1.45, depth: 0.34, height: baseY / 5, color: accent, strokeWidth: 0.22 });
  }
  if (profile.fence) {
    drawing += isoBox({ cx: -4.85, cz: siteZ - 0.25, y: 0, width: 4.45, depth: 0.14, height: 0.55, color: lowerWall, strokeWidth: 0.28 });
    drawing += isoBox({ cx: 5.02, cz: siteZ - 0.25, y: 0, width: 4.2, depth: 0.14, height: 0.55, color: lowerWall, strokeWidth: 0.28 });
  }
  drawing += plant(-5.95, 4.45, 0.62, false) + plant(5.55, 4.6, 0.58, false) + plant(-4.65, 5.05, 0.5, false) + '</g>';
  return drawing;
}

function storefront(template, colors) {
  const [, primary, accent, warm] = colors;
  const type = template.generatorType;
  let interior = '';
  if (type === 'laundry') {
    interior = [112, 164, 216].map((x) => `<rect x="${x}" y="157" width="40" height="57" rx="5" fill="#f7fafc" stroke="#173557" stroke-width="3"/><circle cx="${x + 20}" cy="187" r="13" fill="#92d8e9" stroke="#173557" stroke-width="3"/>`).join('');
  } else if (type === 'barbershop') {
    interior = [125, 185, 245].map((x) => `<rect x="${x - 15}" y="136" width="30" height="42" rx="15" fill="#c8edf4" stroke="#173557" stroke-width="3"/><path d="M${x - 18} 191h36v25h-36z" fill="${primary}"/><path d="M${x} 215v23" stroke="#173557" stroke-width="4"/>`).join('');
  } else if (type === 'cafe' || type === 'food') {
    interior = `<path d="M102 196h156v20H102z" fill="${warm}" stroke="#173557" stroke-width="3"/><path d="M124 216v24m112-24v24" stroke="#173557" stroke-width="5"/>${[125,180,235].map((x) => `<circle cx="${x}" cy="175" r="15" fill="${accent}"/><path d="M${x} 190v28" stroke="#173557" stroke-width="4"/>`).join('')}`;
  } else if (type === 'office') {
    interior = `${windowGrid(110, 145, 5, 3)}<path d="M102 226h156" stroke="${accent}" stroke-width="8"/>`;
  } else {
    interior = `${[112,165,218].map((x) => `<rect x="${x}" y="151" width="38" height="66" fill="#fff" stroke="#173557" stroke-width="3"/><path d="M${x + 6} 169h26m-26 17h26m-26 17h26" stroke="${accent}" stroke-width="5"/>`).join('')}<rect x="266" y="176" width="34" height="62" fill="${warm}" stroke="#173557" stroke-width="3"/>`;
  }
  return `
    <path d="M58 246h244" stroke="#9aa6ae" stroke-width="15" stroke-linecap="round"/>
    <path d="M78 111h204v130H78z" fill="#fefcf8" stroke="#173557" stroke-width="5"/>
    <path d="M68 104h224v45H68z" fill="${primary}" stroke="#173557" stroke-width="5"/>
    <path d="M74 104v45m30-45v45m30-45v45m30-45v45m30-45v45m30-45v45m30-45v45m30-45v45" stroke="#fff" stroke-width="12" opacity=".8"/>
    ${interior}
    <rect x="86" y="71" width="188" height="43" rx="12" fill="#173557"/><path d="M123 91h114" stroke="#fff" stroke-width="8" stroke-linecap="round"/>
  `;
}

function institutional(template, colors) {
  const [, primary, accent, warm] = colors;
  const religious = template.generatorType === 'religious';
  const levels = Math.min(3, template.floors);
  return `
    <path d="M42 254h276" stroke="#99ad92" stroke-width="14" stroke-linecap="round"/>
    <path d="M72 ${115 - levels * 8}h216v${132 + levels * 8}H72z" fill="#fffaf2" stroke="#173557" stroke-width="5"/>
    ${religious ? `<path d="M70 118 180 62l110 56" fill="${primary}" stroke="#173557" stroke-width="5"/><path d="M154 75q26-37 52 0" fill="${warm}" stroke="#173557" stroke-width="4"/>` : `<path d="M61 ${116 - levels * 8}h238v22H61z" fill="${primary}" stroke="#173557" stroke-width="5"/>`}
    ${windowGrid(95, 137, 6, levels, '#b6e1ea')}
    <rect x="161" y="207" width="38" height="40" fill="${warm}" stroke="#173557" stroke-width="3"/>
    <path d="M80 247h200" stroke="${accent}" stroke-width="7"/>
  `;
}

export function renderTemplatePreview(template) {
  const colors = PALETTES[template.previewSeed % PALETTES.length];
  const referenceHouse = template.generatorType === 'house' && template.houseProfile;
  const background = referenceHouse ? '#f3f4f2' : colors[0];
  let content;
  if (referenceHouse) content = houseReferenceScene(template);
  else if (template.generatorType === 'house' || template.generatorType === 'villa') content = genericHouseScene(template, colors, template.generatorType === 'villa');
  else if (['cafe', 'food', 'laundry', 'barbershop', 'retail', 'office', 'service', 'studio', 'commercial'].includes(template.generatorType)) content = storefront(template, colors);
  else content = institutional(template, colors);
  const decoration = referenceHouse
    ? '<path d="M36 258h288" stroke="#737b7c" stroke-width="1" opacity=".08"/>'
    : '<circle cx="312" cy="44" r="24" fill="#fff" opacity=".56"/><path d="M24 52h79" stroke="#fff" stroke-width="8" stroke-linecap="round" opacity=".75"/><path d="M26 259h308" stroke="#173557" stroke-width="2" opacity=".14"/>';
  return `<svg class="template-preview-svg" viewBox="0 0 360 280" role="img" aria-label="${escapeHtml(template.name)} preview" xmlns="http://www.w3.org/2000/svg"><rect width="360" height="280" rx="24" fill="${background}"/>${content}${decoration}</svg>`;
}

export function renderHeroVisual() {
  const template = { name: 'KIPS DESIGN procedural home', generatorType: 'villa', previewSeed: 3, floors: 2 };
  return renderTemplatePreview(template);
}
